<template>
  <!-- 弹幕滚动条（漫画分镜 / 对话框风格） -->
  <div
    v-show="danmakuEnabled"
    :ref="bindDanmakuBar"
    class="reader-danmaku-bar"
    :class="{
      'reader-danmaku-bar--with-top-bar': showTopBar,
      'reader-danmaku-bar--interact-open': danmakuInteractOpen
    }"
  >
    <div class="reader-danmaku-bar__shell">
      <div
        class="reader-danmaku-bar__inner"
        :class="'reader-danmaku-bar__inner--rows-' + danmakuEffectiveTrackCount"
        :style="danmakuBarInnerAreaStyle"
      >
        <div class="reader-danmaku-tracks">
          <div
            v-for="lineIdx in danmakuRowIndices"
            :key="'danmaku-line-' + lineIdx"
            class="reader-danmaku-track"
            :class="'reader-danmaku-track--line-' + lineIdx"
          >
            <div
              class="reader-danmaku-scroll"
              :style="danmakuLineScrollStyle(lineIdx)"
            >
              <span
                v-for="seg in danmakuSegmentsForTrack(lineIdx)"
                :key="seg._k"
                class="reader-danmaku-item"
                :class="{
                  'reader-danmaku-item--highlight': seg.highlight || seg.isMine,
                  'reader-danmaku-item--banner': seg.isBanner,
                  'reader-danmaku-item--placeholder': seg.isPlaceholder
                }"
                :style="danmakuItemInlineStyle(seg)"
                @click.stop="onDanmakuSegmentClick(seg)"
              >
                <span class="reader-danmaku-item__text">{{ seg.text }}</span>
                <span
                  v-if="Number(seg.likeCount) > 0"
                  class="reader-danmaku-item__like-inline"
                  :aria-label="'点赞 ' + seg.likeCount"
                >
                  <AppIcon
                    name="like"
                    size="xs"
                    class="reader-danmaku-item__like-ico"
                    aria-label="点赞数"
                  />
                  <span class="reader-danmaku-item__like-num">{{ seg.likeCount }}</span>
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="danmakuInteractOpen"
        class="reader-danmaku-interact-panel reader-danmaku-interact-panel--below"
        role="dialog"
        aria-modal="true"
        @click.stop
      >
        <p
          class="reader-danmaku-interact-quote"
          :title="danmakuInteractContent"
        >{{ danmakuInteractContent }}</p>
        <div class="reader-danmaku-interact-side">
          <button
            type="button"
            class="reader-danmaku-interact-like"
            :class="{ 'is-on': danmakuInteractIsLiked }"
            :disabled="danmakuInteractLikedLoading"
            aria-label="点赞弹幕"
            @click="toggleDanmakuInteractLike"
          >
            <AppIcon
              name="like"
              size="sm"
              :variant="danmakuInteractIsLiked ? 'active' : 'default'"
              aria-label="点赞弹幕"
            />
            <span class="reader-danmaku-interact-like-count">{{ danmakuInteractLikeCount }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 发射弹幕：由大到小飞入 -->
  <div
    v-if="danmakuFlyActive"
    class="reader-danmaku-fly"
    :class="{ 'reader-danmaku-fly--playing': danmakuFlyPlaying }"
    :style="danmakuFlyVars"
    @animationend="onDanmakuFlyAnimationEnd"
  >
    <span class="reader-danmaku-fly__text">{{ danmakuFlyText }}</span>
  </div>

  <div
    v-if="danmakuInteractOpen"
    class="reader-danmaku-interact-mask"
    @click="closeDanmakuInteract"
  />

  <div
    class="reader-danmaku-fab-stack"
    :class="{ 'reader-danmaku-fab-stack--visible': danmakuFabVisible }"
    @click.stop
  >
    <button
      type="button"
      class="reader-danmaku-fab reader-danmaku-fab--pill reader-danmaku-fab--toggle"
      :class="{ 'reader-danmaku-fab--toggle-off': !danmakuEnabled }"
      :aria-pressed="danmakuEnabled"
      :aria-label="danmakuEnabled ? '弹幕显示已开启，点击关闭' : '弹幕显示已关闭，点击开启'"
      @click="toggleDanmakuVisibilityFab"
    >
      <span class="reader-danmaku-fab__main">弹</span>
      <AppIcon
        name="danmakuToggle"
        size="sm"
        :variant="danmakuEnabled ? 'active' : 'default'"
        class="reader-danmaku-fab__ico reader-danmaku-fab__ico--switch"
        aria-label=""
      />
    </button>
    <button
      type="button"
      class="reader-danmaku-fab reader-danmaku-fab--pill reader-danmaku-fab--settings"
      aria-label="弹幕显示设置"
      @click="showDanmakuSettingsDialog = true"
    >
      <span class="reader-danmaku-fab__main">弹</span>
      <AppIcon
        name="danmakuSettings"
        size="sm"
        :variant="showDanmakuSettingsDialog ? 'active' : 'default'"
        class="reader-danmaku-fab__ico reader-danmaku-fab__ico--gear"
        aria-label=""
      />
    </button>
    <button
      type="button"
      class="reader-danmaku-fab reader-danmaku-fab--pill reader-danmaku-fab--send"
      aria-label="发射弹幕"
      @click="openDanmakuSendDialog"
    >
      <span class="reader-danmaku-fab__main">发</span>
      <span class="reader-danmaku-fab__sub">弹幕</span>
    </button>
  </div>

  <el-dialog
    v-model="showDanmakuSendDialog"
    width="90%"
    :style="{ maxWidth: '440px' }"
    append-to-body
    destroy-on-close
    class="reader-danmaku-dialog reader-danmaku-dialog--comic-send"
    :show-close="true"
    align-center
    @closed="danmakuDraft = ''"
  >
    <template #header>
      <div class="reader-danmaku-comic-title-row reader-danmaku-comic-title-row--send">
        <div class="reader-danmaku-comic-heading">
          <span class="reader-danmaku-comic-heading__main">台词发射中</span>
        </div>
      </div>
    </template>
    <div class="reader-danmaku-comic-bubble">
      <el-input
        id="reader-danmaku-input"
        v-model="danmakuDraft"
        type="textarea"
        :rows="4"
        maxlength="80"
        show-word-limit
        placeholder="吐槽、应援、名场面打卡……"
        class="reader-danmaku-comic-textarea"
        aria-label="输入弹幕内容"
      />
    </div>
    <template #footer>
      <div class="reader-danmaku-comic-footer">
        <button
          type="button"
          class="reader-danmaku-punch reader-danmaku-punch--go"
          @click="submitDanmakuSend"
        >
          <AppIcon name="danmakuSend" size="sm" class="reader-danmaku-punch__rocket" aria-label="" />
          发射
        </button>
      </div>
    </template>
  </el-dialog>

  <el-dialog
    v-model="showDanmakuSettingsDialog"
    width="90%"
    :style="{ maxWidth: '440px' }"
    append-to-body
    class="reader-danmaku-dialog reader-danmaku-dialog--comic-settings"
    align-center
    @opened="onDanmakuSettingsOpened"
    @closed="onDanmakuSettingsClosed"
  >
    <template #header>
      <div class="reader-danmaku-comic-title-row reader-danmaku-comic-title-row--settings">
        <div class="reader-danmaku-comic-heading">
          <span class="reader-danmaku-comic-heading__main">弹幕显示</span>
        </div>
      </div>
    </template>
    <div class="reader-danmaku-settings-form reader-danmaku-settings-form--comic">
      <div class="reader-danmaku-settings-lr">
        <span class="reader-danmaku-settings-label">不透明度</span>
        <div class="reader-danmaku-settings-control reader-danmaku-settings-control--slider">
          <div class="reader-danmaku-settings-slider-row">
            <el-slider
              v-model="danmakuTextOpacity"
              :min="0.25"
              :max="1"
              :step="0.05"
              :format-tooltip="(v) => Math.round(v * 100) + '%'"
            />
            <span class="reader-danmaku-settings-value">{{ Math.round(danmakuTextOpacity * 100) }}%</span>
          </div>
        </div>
      </div>
      <div class="reader-danmaku-settings-lr">
        <span class="reader-danmaku-settings-label">显示区域</span>
        <div class="reader-danmaku-settings-control reader-danmaku-settings-control--slider">
          <div class="reader-danmaku-settings-slider-row">
            <el-slider
              v-model="danmakuDisplayAreaPercent"
              :min="5"
              :max="45"
              :step="1"
              :format-tooltip="danmakuDisplayAreaSliderTooltip"
            />
            <span class="reader-danmaku-settings-value">{{ danmakuDisplayAreaPercentClamped }}%</span>
          </div>
        </div>
      </div>
      <div class="reader-danmaku-settings-lr">
        <span class="reader-danmaku-settings-label">字体大小</span>
        <div class="reader-danmaku-settings-control reader-danmaku-settings-control--slider">
          <div class="reader-danmaku-settings-slider-row">
            <el-slider
              v-model="danmakuFontSizeEnum"
              :min="1"
              :max="4"
              :step="1"
              :format-tooltip="danmakuFontSizeSliderTooltip"
            />
            <span class="reader-danmaku-settings-value">{{ danmakuFontSizeEnumLabel(danmakuFontSizeEnum) }}</span>
          </div>
        </div>
      </div>
      <div class="reader-danmaku-settings-lr">
        <span class="reader-danmaku-settings-label">弹幕速度</span>
        <div class="reader-danmaku-settings-control reader-danmaku-settings-control--slider">
          <div class="reader-danmaku-settings-slider-row">
            <el-slider
              v-model="danmakuSpeedEnum"
              :min="1"
              :max="5"
              :step="1"
              :format-tooltip="danmakuSpeedSliderTooltip"
            />
            <span class="reader-danmaku-settings-value">{{ danmakuSpeedEnumLabel(danmakuSpeedEnum) }}</span>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import AppIcon from '@/shared/components/AppIcon.vue'

const showDanmakuSendDialog = defineModel('showDanmakuSendDialog', { type: Boolean, default: false })
const showDanmakuSettingsDialog = defineModel('showDanmakuSettingsDialog', { type: Boolean, default: false })
const danmakuDraft = defineModel('danmakuDraft', { type: String, default: '' })
const danmakuTextOpacity = defineModel('danmakuTextOpacity', { type: Number, required: true })
const danmakuDisplayAreaPercent = defineModel('danmakuDisplayAreaPercent', { type: Number, required: true })
const danmakuFontSizeEnum = defineModel('danmakuFontSizeEnum', { type: Number, required: true })
const danmakuSpeedEnum = defineModel('danmakuSpeedEnum', { type: Number, required: true })

const props = defineProps({
  showTopBar: { type: Boolean, default: false },
  danmakuEnabled: { type: Boolean, default: true },
  danmakuBarRef: { type: Object, default: null },
  danmakuInteractOpen: { type: Boolean, default: false },
  danmakuEffectiveTrackCount: { type: Number, default: 1 },
  danmakuBarInnerAreaStyle: { type: Object, default: () => ({}) },
  danmakuRowIndices: { type: Array, default: () => [] },
  danmakuInteractContent: { type: String, default: '' },
  danmakuInteractIsLiked: { type: Boolean, default: false },
  danmakuInteractLikedLoading: { type: Boolean, default: false },
  danmakuInteractLikeCount: { type: Number, default: 0 },
  danmakuFlyActive: { type: Boolean, default: false },
  danmakuFlyPlaying: { type: Boolean, default: false },
  danmakuFlyText: { type: String, default: '' },
  danmakuFlyVars: { type: Object, default: () => ({}) },
  danmakuFabVisible: { type: Boolean, default: false },
  danmakuDisplayAreaPercentClamped: { type: Number, default: 0 },
  danmakuLineScrollStyle: { type: Function, required: true },
  danmakuSegmentsForTrack: { type: Function, required: true },
  danmakuItemInlineStyle: { type: Function, required: true },
  danmakuDisplayAreaSliderTooltip: { type: Function, required: true },
  danmakuFontSizeSliderTooltip: { type: Function, required: true },
  danmakuSpeedSliderTooltip: { type: Function, required: true },
  danmakuFontSizeEnumLabel: { type: Function, required: true },
  danmakuSpeedEnumLabel: { type: Function, required: true },
  onDanmakuSegmentClick: { type: Function, required: true },
  closeDanmakuInteract: { type: Function, required: true },
  toggleDanmakuInteractLike: { type: Function, required: true },
  toggleDanmakuVisibilityFab: { type: Function, required: true },
  openDanmakuSendDialog: { type: Function, required: true },
  onDanmakuSettingsOpened: { type: Function, required: true },
  onDanmakuSettingsClosed: { type: Function, required: true },
  submitDanmakuSend: { type: Function, required: true },
  onDanmakuFlyAnimationEnd: { type: Function, required: true },
})

function bindDanmakuBar(el) {
  if (props.danmakuBarRef) {
    props.danmakuBarRef.value = el
  }
}
</script>
